import moment from 'moment';
import React, { useEffect, useState } from 'react';
import '@brands/Dashboard/MultiStage/multiStage.scss';
import { caseTypeLabel, statusLabel, statusVariant } from '@brands/Dashboard/MultiStage/multiStageUi';
import { tokens } from '@brands/Dashboard/MultiStage/multiStageTokens';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './styles.module.scss';

import Container from '@brands/Components/Container/Container';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import useLoadDependents from '@brands/hooks/useLoadDependents';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { CaseJourneysResponse, getCaseJourneys } from '@brands/services/cases/multiStage';
import { ICase, Status } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectDependents } from '@brands/store/selectors/dependents';
import { getFormatDateFullDate } from '@brands/Utils/formatDate';

import { startLinkedCaseCompletion } from '@brands/Dashboard/MultiStage/utils/completeLinkedCase';
import EditCreditCardModal from '../Appointments/Patient/EditCreditCardModal/EditCreditCardModal';
import UpcomingServices from '../Dashboard/Patient/components/UpcomingServices';
import { isClosedJourney } from './Sections/useCaseJourneyLookup';

const formatDate = (value: string | null): string => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return value;
  }
};

type JourneyMessage = { title: string; body?: string };

// Why a notification link's case no longer opens the wizard, e.g. a
// "Schedule my appointment" email clicked after the visit was booked.
const linkedCaseNotice = (linkedCase: ICase, timeZone: string): JourneyMessage => {
  if (linkedCase.status === Status.Completed) return { title: 'This case is complete' };
  if (linkedCase.status === Status.Canceled) return { title: 'This case was canceled' };
  if (linkedCase.video_conference_date) {
    // The same one-hour window the server's reschedule lockout uses.
    const minutesAway = moment.utc(linkedCase.video_conference_date).diff(moment(), 'minutes', true);
    const lockedOut = minutesAway > 0 && minutesAway <= 60;
    return {
      title: 'This appointment is already booked',
      body: `Your ${caseTypeLabel(linkedCase.type)} appointment is on ${getFormatDateFullDate(
        linkedCase.video_conference_date,
        timeZone,
      )}.${lockedOut ? " Appointments can't be rescheduled within one hour of their start time." : ''}`,
    };
  }
  return { title: 'Nothing else is needed from you for this case right now' };
};

const actionButtonStyle: React.CSSProperties = {
  background: tokens.info,
  color: '#fff',
  border: 'none',
  borderRadius: tokens.radiusPill,
  padding: '10px 22px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

// DO-3883: dedicated "My care journey" screen — lists all linked cases of a
// journey with full per-case actions (Join / edit appointment / cancel) by
// reusing the existing UpcomingServices case cards.
const MyCareJourney = (): JSX.Element => {
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Prefer in-app navigation state, but fall back to URL query params so a
  // refresh, bookmark, or shared link (which drops `location.state`) can still
  // resolve the journey instead of rendering an empty page.
  const parseNumericParam = (name: string): number | undefined => {
    const raw = searchParams.get(name);
    if (raw == null || raw === '') return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  };
  const patientId: number | undefined = (state as any)?.patientId ?? parseNumericParam('patientId');
  const journeyId: number | undefined = (state as any)?.journeyId ?? parseNumericParam('journeyId');
  // Set by notification links (email, SMS, in-app) that should open this
  // case's intake wizard rather than the journey overview.
  const completeCaseId: number | undefined = parseNumericParam('completeCaseId');
  const journeyKey = `${patientId}:${journeyId}`;

  const { userInfo } = useAppSelector(selectAuth);
  useLoadDependents();
  const { dependents: listOfDependents } = useAppSelector(selectDependents);
  const patientLabel: string | undefined =
    patientId === Number(userInfo.id)
      ? undefined
      : listOfDependents.find((dependent) => Number(dependent?.value) === patientId)?.label ?? undefined;

  const [loading, setLoading] = useState<boolean>(true);
  // Distinguishes the first mount fetch (blocks the whole screen) from later
  // reloads (kept inline so a background refresh never blanks the page).
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [cases, setCases] = useState<ICase[]>([]);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [editCreditCardModal, setEditCreditCardModal] = useState<boolean>(false);
  const [selectedCase, setSelectedCase] = useState<ICase>();
  // Cases in this journey with outstanding intake — drives the "Action
  // needed" banner and the matching per-case badge/button.
  const [needsIntakeCaseIds, setNeedsIntakeCaseIds] = useState<Set<number>>(new Set());
  const [actionNeededCase, setActionNeededCase] = useState<ICase | undefined>(undefined);
  // The journey `cases` was last loaded for, so a completeCaseId that
  // arrives along with a different journeyId waits for that journey's cases.
  const [loadedJourneyKey, setLoadedJourneyKey] = useState<string>();
  const [startingIntake, setStartingIntake] = useState<boolean>(false);
  // Without these the page drew an empty card, with no explanation, when
  // the journey couldn't be loaded or isn't visible to this account.
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [journeyMissing, setJourneyMissing] = useState<boolean>(false);
  // The case a notification link asked to complete that no longer needs it.
  const [noticeCaseId, setNoticeCaseId] = useState<number>();

  const clearCases = (): void => {
    setCases([]);
    setNeedsIntakeCaseIds(new Set());
    setActionNeededCase(undefined);
  };

  const load = async (): Promise<void> => {
    if (!patientId) {
      setJourneyMissing(true);
      setLoading(false);
      setInitialLoad(false);
      setLoadedJourneyKey(journeyKey);
      return;
    }
    setLoading(true);
    try {
      const journeys: CaseJourneysResponse = await getCaseJourneys(patientId);
      // Only default to the first journey when no specific id was requested;
      // a passed-but-missing journeyId must not silently show another journey.
      const journey =
        journeyId != null ? journeys.multi_stage.find((g) => g.case_journey_id === journeyId) : journeys.multi_stage[0];
      setJourneyMissing(!journey);
      if (journey) {
        setStartedAt(journey.started_at);
        setStatus(journey.status);
        const full = (await Promise.all(journey.cases.map((c) => getOneCase(c.id)))).filter(Boolean) as ICase[];
        setCases(full);

        // Closed cases can't need action; checking only the open ones keeps
        // this to the handful of cases actually still in play. `Upcoming` is
        // the single ground-truth "patient action required" signal — it
        // covers a payment-only follow-up case too, which a per-case
        // getCaseIntake lookup on needs_intake alone would have missed. A free
        // care coordination follow-up stays Upcoming until the patient has
        // seen its Jotform link, which the wizard skips straight to.
        const openCases = full.filter((c) => c.status !== Status.Completed && c.status !== Status.Canceled);
        const needsAction = openCases.filter((c) => c.status === Status.Upcoming);
        setNeedsIntakeCaseIds(new Set(needsAction.map((c) => c.id)));
        setActionNeededCase(needsAction[0]);
      } else {
        clearCases();
      }
      setLoadFailed(false);
    } catch {
      // A failed background reload keeps this journey's cases on screen, but
      // a failed switch from another journey must not keep showing that one.
      if (loadedJourneyKey !== journeyKey) clearCases();
      setLoadFailed(true);
    } finally {
      setLoading(false);
      setInitialLoad(false);
      setLoadedJourneyKey(journeyKey);
    }
  };

  useEffect(() => {
    setNoticeCaseId(undefined);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, journeyId]);

  // Routes any case still in CaseStatus.upcoming through the normal
  // case-creation wizard instead of the (removed) standalone intake page —
  // pre-fills clinical/payment answers from the previous case in the journey
  // and locks the patient picker to this case's patient. See
  // completeLinkedCase.ts.
  const goToCompleteAction = async (actionCase: ICase): Promise<void> => {
    await startLinkedCaseCompletion(actionCase, dispatch, roleNavigate);
  };

  // Opens the wizard a notification link asked for, the same as that case's
  // Complete Intake button. The param is dropped first so Back from the
  // wizard returns to the journey instead of relaunching it. A case that no
  // longer needs action (already scheduled, completed or canceled) leaves
  // the patient on the journey, with a notice saying why.
  useEffect(() => {
    if (completeCaseId == null || loadedJourneyKey !== journeyKey) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('completeCaseId');
    setSearchParams(nextParams, { replace: true, state });
    const linkedCase = cases.find((c) => Number(c.id) === completeCaseId);
    if (linkedCase && needsIntakeCaseIds.has(linkedCase.id)) {
      setStartingIntake(true);
      startLinkedCaseCompletion(linkedCase, dispatch, roleNavigate).catch(() => setStartingIntake(false));
    } else {
      setNoticeCaseId(linkedCase?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeCaseId, loadedJourneyKey, journeyKey]);

  // Block the whole screen with a centered spinner on the first load, so the
  // page never flashes a bare title over a blank body. Later reloads keep the
  // page mounted and surface their progress inline via UpcomingServices. A
  // pending completeCaseId keeps the spinner up until the wizard opens.
  if (initialLoad || completeCaseId != null || startingIntake) {
    return <Loading fullScreen />;
  }

  const noticeCase = cases.find((c) => c.id === noticeCaseId && !needsIntakeCaseIds.has(c.id));
  const notice = noticeCase && linkedCaseNotice(noticeCase, Intl.DateTimeFormat().resolvedOptions().timeZone);
  let unavailable: (Required<JourneyMessage> & { action: string; onAction: () => void }) | undefined;
  if (!cases.length && loadFailed) {
    unavailable = {
      title: "We couldn't load this care journey",
      body: 'Something went wrong on our end. Please try again.',
      action: 'Try again',
      onAction: load,
    };
  } else if (!cases.length && journeyMissing) {
    unavailable = {
      title: "We couldn't find this care journey",
      body: "It may belong to a different account. Make sure you're signed in as the patient this link was meant for, or as their guardian.",
      action: 'Go to My Care',
      onAction: () => roleNavigate('/my-care'),
    };
  }

  return (
    <Container patientDashboard>
      <div className={`${styles.myCareJourneyDiv} ms-scope`}>
        <button
          type="button"
          onClick={() => (isClosedJourney(status) ? roleNavigate('/my-care#care_history') : navigate(-1))}
          style={{
            background: 'none',
            border: 'none',
            color: tokens.info,
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: 12,
          }}
        >
          &lt; {isClosedJourney(status) ? 'Back to care history' : 'Back to upcoming appointments'}
        </button>
        <h1 style={{ fontSize: 32, marginBottom: 16 }}>
          {patientLabel ? `${patientLabel}'s care journey` : 'My care journey'}
        </h1>

        <div className="ms-card">
          {unavailable ? (
            <div role="alert">
              <p style={{ margin: 0, fontWeight: 700, color: tokens.navy }}>{unavailable.title}</p>
              <p style={{ margin: '4px 0 12px', color: tokens.gray700, fontSize: 14 }}>{unavailable.body}</p>
              <button type="button" onClick={unavailable.onAction} style={actionButtonStyle}>
                {unavailable.action}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              {startedAt && <p style={{ margin: 0, color: tokens.gray700 }}>Started {formatDate(startedAt)}</p>}
              {status && <span className={`ms-pill ${statusVariant(status)}`}>{statusLabel(status)}</span>}
            </div>
          )}

          {notice && (
            <div
              role="status"
              style={{
                background: tokens.infoBg,
                border: `1px solid ${tokens.infoBorder}`,
                borderRadius: tokens.radiusMd,
                padding: '16px 20px',
                marginBottom: 16,
              }}
            >
              <p style={{ margin: 0, fontWeight: 700, color: tokens.navy }}>{notice.title}</p>
              {notice.body && <p style={{ margin: '4px 0 0', color: tokens.gray700, fontSize: 14 }}>{notice.body}</p>}
            </div>
          )}

          {actionNeededCase && (
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                background: tokens.warningBg,
                border: `1px solid ${tokens.warning}`,
                borderRadius: tokens.radiusMd,
                padding: '16px 20px',
                marginBottom: 16,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: tokens.danger,
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                !
              </span>
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: tokens.navy }}>
                  Action needed to schedule your next visit
                </p>
                <p style={{ margin: '4px 0 12px', color: tokens.gray700, fontSize: 14 }}>
                  We can&apos;t book your {caseTypeLabel(actionNeededCase.type)} appointment until you finish your
                  intake.
                </p>
                <button type="button" onClick={() => goToCompleteAction(actionNeededCase)} style={actionButtonStyle}>
                  Complete Intake
                </button>
              </div>
            </div>
          )}

          {!unavailable && (
            <UpcomingServices
              displayingCases={cases}
              dataLoading={loading}
              reloadAll={load}
              setEditCreditCardModal={setEditCreditCardModal}
              selectedCaseFromUpcomingServices={selectedCase}
              setSelectedCaseFromUpcomingServices={setSelectedCase}
              isLoading={loading}
              needsIntakeCaseIds={needsIntakeCaseIds}
              showCareCoordinationScheduleStatus
            />
          )}
        </div>

        {editCreditCardModal && selectedCase && (
          <EditCreditCardModal
            setOpenModal={setEditCreditCardModal}
            isOpen={editCreditCardModal}
            thisCase={selectedCase}
            reloadAll={load}
            refreshAll={load}
          />
        )}
      </div>
    </Container>
  );
};

export default MyCareJourney;
